import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ModifyConsolidatedForecastView } from '@futrli/views/views/forecasts/ModifyConsolidatedForecastView/ModifyConsolidatedForecastView';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { useDuplicateForecastForm } from '../hooks/use-duplicate-forecast-form';

interface DuplicateForecastContextProps {
  children?: ReactNode;
  forecastId: string;
}

interface DuplicateForecastContextValues {
  form: ReturnType<typeof useDuplicateForecastForm>;
  isConsolidation: boolean;
  organisationId: string;
  view: ModifyForecastView | ModifyConsolidatedForecastView;
  workspaceId: string;
}

const defaultState = ({} as unknown) as DuplicateForecastContextValues;

export const DuplicateForecastContext = createContext(defaultState);

export const DuplicateForecastContextProvider = ({ children, forecastId }: DuplicateForecastContextProps) => {
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const ViewClass = isConsolidation ? ModifyConsolidatedForecastView : ModifyForecastView;
  const view = useApiView(
    useMemo(() => new ViewClass(workspaceId, organisationId, forecastId), [
      ViewClass,
      workspaceId,
      organisationId,
      forecastId
    ])
  );

  const form = useDuplicateForecastForm(view.state.forecast, isConsolidation);

  const values = {
    form,
    isConsolidation,
    organisationId,
    view,
    workspaceId
  };

  return <DuplicateForecastContext.Provider value={values}>{children}</DuplicateForecastContext.Provider>;
};

export const useDuplicateForecastContext = () => useContext(DuplicateForecastContext);
